import Layout from '../../components/feature/Layout';
import SEO from '../../components/feature/SEO';

const APP_URL = 'https://app.safelinesolutions.net';

const categorias = [
  {
    icon: 'ri-search-eye-line',
    title: 'Investigación y Análisis',
    items: [
      'Investigación de incidentes: línea de tiempo, causa raíz y acciones correctivas',
      'Análisis de patrones y oportunidades preventivas',
      'Métricas HSE: TRIR, LTIFR, tasas de accidentabilidad',
    ],
  },
  {
    icon: 'ri-shield-flash-line',
    title: 'Riesgos y Operaciones',
    items: [
      'Matrices IPERC: peligros, riesgos, controles y criticidad',
      'Permisos de trabajo y procedimientos',
      'Diseño de flujos de proceso',
    ],
  },
  {
    icon: 'ri-file-shield-2-line',
    title: 'Cumplimiento y Auditoría',
    items: [
      'Auditorías HSE con checklist y hallazgos',
      'Revisión de contratos',
      'Cumplimiento legal en Latinoamérica',
      'Control de versiones documentales',
    ],
  },
  {
    icon: 'ri-file-chart-2-line',
    title: 'Documentación e Informes',
    items: [
      'Resúmenes ejecutivos para gerencia',
      'Adaptación y traducción de contenido técnico',
      'Generación de informes profesionales',
    ],
  },
];

const safelineAiHubJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Safeline Ai Hub',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: APP_URL,
  description:
    'Plataforma de Inteligencia Artificial que automatiza documentación y cumplimiento HSE: investigación de incidentes, matrices IPERC, auditorías e informes ejecutivos listos en Word, Excel y PowerPoint.',
  provider: {
    '@type': 'Organization',
    name: 'SafeLine Solutions',
    url: `${import.meta.env.VITE_SITE_URL ?? ''}`,
  },
  areaServed: {
    '@type': 'Place',
    name: 'Latinoamérica',
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: `${import.meta.env.VITE_SITE_URL ?? ''}/` },
      { '@type': 'ListItem', position: 2, name: 'Safeline Ai Hub', item: `${import.meta.env.VITE_SITE_URL ?? ''}/safeline-ai-hub` },
    ],
  },
};

export default function SafelineAiHubPage() {
  return (
    <Layout>
      <SEO
        title="Safeline Ai Hub | Copiloto de IA para HSE - SafeLine Solutions"
        description="Safeline Ai Hub es la plataforma de IA de SafeLine Solutions: convierte un caso HSE en un entregable profesional (Word, Excel, PowerPoint) en minutos. Investigación de incidentes, matrices IPERC, auditorías e informes."
        keywords="Safeline Ai Hub, IA para HSE, copiloto HSE, IPERC con IA, investigación de incidentes IA, auditorías HSE con IA"
        canonical="/safeline-ai-hub"
        jsonLd={safelineAiHubJsonLd}
      />

      <div className="bg-white">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 to-teal-900 text-white py-24 pt-40">
          <div className="absolute inset-0 bg-teal-500/10 mix-blend-soft-light" />

          <div className="relative max-w-5xl mx-auto px-6 text-center">
            <span className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-300 px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-flashlight-line"></i>
              Plataforma externa · Powered by IA
            </span>

            <h1 className="text-5xl lg:text-7xl font-extrabold mt-6 leading-tight">
              Safeline <span className="text-teal-400">Ai Hub</span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-300 mt-8 leading-relaxed max-w-3xl mx-auto">
              Del caso al entregable HSE, en minutos.
            </p>

            <p className="text-white/80 text-base mt-6 max-w-2xl mx-auto leading-relaxed">
              El copiloto de prevención de riesgos que domina IPERC, ATS,
              investigación de incidentes y normativa. Se apoya en los
              documentos reales de tu organización —citando la fuente— y te
              entrega el Word, Excel o PowerPoint listo para presentar.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-teal-400 text-gray-900 px-10 py-4 rounded-lg text-base font-bold hover:bg-teal-300 transition-all inline-flex items-center gap-2 whitespace-nowrap shadow-lg shadow-teal-400/40 hover:shadow-teal-300/50 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                <i className="ri-external-link-line text-xl"></i>
                Entrar a Safeline Ai Hub
              </a>

              <span className="text-sm text-white/60">
                Se abre en una pestaña nueva · app.safelinesolutions.net
              </span>
            </div>
          </div>
        </section>

        {/* CATEGORÍAS */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                20 skills activas
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Todo lo que un profesional HSE <span className="text-teal-600">necesita entregar</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Organizado en cuatro áreas de trabajo, listo para producir documentos
                profesionales a partir de tus propios procedimientos.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {categorias.map((categoria) => (
                <div
                  key={categoria.title}
                  className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-5">
                    <i className={`${categoria.icon} text-2xl text-white`}></i>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {categoria.title}
                  </h3>

                  <ul className="space-y-2.5">
                    {categoria.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-gray-600 leading-relaxed">
                        <i className="ri-check-line text-teal-600 text-base flex-shrink-0 mt-0.5"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-24 bg-gradient-to-br from-teal-700 via-teal-600 to-teal-800 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-80 h-80 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
          </div>

          <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Dirigido a profesionales HSE en Latinoamérica
            </h2>

            <p className="text-lg md:text-xl text-white/85 mb-10 leading-relaxed">
              Safeline Ai Hub es una plataforma independiente, operada por SafeLine
              Solutions. Ingresa con tu cuenta para generar tu primer entregable.
            </p>

            <a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white text-teal-700 px-10 py-4 rounded-lg text-base font-bold hover:bg-teal-50 transition-all inline-flex items-center gap-3 whitespace-nowrap hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <i className="ri-external-link-line text-xl"></i>
              Entrar a Safeline Ai Hub
              <i className="ri-arrow-right-line text-xl transition-transform group-hover:translate-x-1"></i>
            </a>
          </div>
        </section>
      </div>
    </Layout>
  );
}
